// ============================================
// Path: /frontend/src/pages/AboutPage.jsx
// Purpose: About page with company story and mission
// UPDATED: Mobile-first responsive styling applied
// ============================================

// ========================================
// IMPORTS
// ========================================
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

// ========================================
// MAIN COMPONENT
// ========================================
export default function AboutPage() {
  console.log('🎨 [AboutPage] Component mounted');

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* Page Header */}
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">
            About Us
          </h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
            Our story, your journey
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-6 sm:space-y-8">
          {/* Our Story Section */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              Our Story: A 22-Year Journey
            </h2>
            <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              <p>
                Twenty-two years ago, a dream was born—a vision to create something that would help people find not just vehicles or properties, but <em>possibilities</em>. Life, as it often does, had other plans. The dream took a back seat, and the years quietly slipped by like pages in an unfinished book.
              </p>
              <p>
                But dreams, real ones, don't die. They wait.
              </p>
              <p>
                Many sunrises later, that dream has found its feet. Today, MotoHome Finder stands before you—not as our achievement, but as <em>ours together</em>. Because here's the truth we hold dear: <strong>Without you, there is no us.</strong>
              </p>
            </div>
          </section>

          {/* What We Built Section */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              What We Built For You
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed mb-4 sm:mb-6">
              MotoHome Finder is a listings marketplace. Your one-stop spot where vehicles, properties, and investment opportunities live.
            </p>
            <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              But here's where it gets interesting. We realized that finding something you love is one thing—being able to actually get it? That's another story. So we built some tools to help close that gap:
            </p>
          </section>

          {/* Group Buy Feature */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              Because Dreams Shouldn't Wait for Full Wallets
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              Our Group Buy feature splits the purchase price into shares. You contribute what you can afford, others chip in their share, and when it reaches 100%, legal paperwork is prepared protecting everyone's ownership rights. Your dream doesn't have to wait for your bank account to catch up.
            </p>
          </section>

          {/* Finance Partners Feature */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              When You Need a Hand
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              Maybe the listing you want isn't on Group Buy. Maybe you just need some financing help. Fill out our Finance Application Form and our finance partners will get in touch. Sometimes you just need someone to meet you halfway.
            </p>
          </section>

          {/* Joint Venture Feature */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              Where Land Meets Capital
            </h2>
            <p className="text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              Got land but no construction funds? Or maybe you've got capital but no strategic plot to build on? Our Joint Venture Application Form connects landowners with investors and construction professionals. When the right match clicks, potential becomes reality.
            </p>
          </section>

          {/* Closing Message */}
          <section className="bg-muted/40 rounded-lg p-3 sm:p-4 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold mb-2 sm:mb-3 text-foreground">
              A Word From the Heart
            </h2>
            <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm md:text-base text-foreground/80 leading-relaxed">
              <p>
                More than anything, we treasure your feedback. You are the voice we must listen to. While we take every precaution to enlist genuine, trustworthy industry players, we know—sadly—that every marketplace has its challenges. If something doesn't feel right, if a transaction raises concerns, please reach out. Together, we'll keep this space transparent, seamless, and trustworthy.
              </p>
              <p className="font-semibold text-foreground">
                Welcome home.
              </p>
              <p>
                Happy searching. And when you get here? Sit down. Stay a while. You may as well call, we have time to bounce ideas over cups of coffee.
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
          <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground text-center">
            Last updated: December 2024
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}